import { useQuery } from '@tanstack/react-query';
import iotApi from '../../api/iotApi.client';
import { FetchInviteUserDetailParams } from '../../api/requests/user/user.requests.types';
import { UserQueryKey } from './user.hooks.keys';

/**
 * React Query hook for fetching the current user's profile information.
 *
 * Uses React Query to manage the async state and caching for the user profile data.
 * The query is automatically cached and can be invalidated/refetched as needed.
 *
 * @returns {UseQueryResult<z.infer<typeof userResponseSchemas.fetchMe>, Error>} React Query result object containing:
 * - `data`: User profile data when successfully loaded
 * - `isLoading`: Loading state indicator
 * - `error`: Error object if the request fails
 * - `refetch`: Function to manually refetch the data
 * - Other standard React Query properties
 *
 * @example
 * ```typescript
 * function UserProfile() {
 *   const { data: userProfile, isLoading, error } = useFetchMe();
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (error) return <div>Error: {error.message}</div>;
 *
 *   return (
 *     <div>
 *       <h1>{userProfile.user.firstname} {userProfile.user.lastname}</h1>
 *       <p>{userProfile.user.email}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export function useFetchMe() {
  return useQuery({
    queryKey: [UserQueryKey.FETCH_ME],
    queryFn: async () => {
      const res = await iotApi.user.fetchMe();

      return res;
    },
  });
}

export function useFetchInviteUserDetail(params: FetchInviteUserDetailParams) {
  return useQuery({
    queryKey: [UserQueryKey.FETCH_INVITE_USER_DETAIL, params.token],
    queryFn: async () => {
      const res = await iotApi.user.fetchInviteUserDetail(params);

      return res;
    },
  });
}
